// src/features/admin/components/UserForm.jsx
import { useState, useMemo, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { BASE_URL } from "shared/lib/config";
import { toast } from "react-toastify";

/** -------------------------------------------
 * Helpers
 * ------------------------------------------ */
function getStore() {
  if (typeof window === "undefined") return localStorage;
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
}

function normalizeSpaces(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ");
}

async function fetchJson(url, token, options = {}) {
  const res = await fetch(url, {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    body: options.body,
  });

  const text = await res.text();

  if (!res.ok) {
    let body = {};
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = {};
    }
    throw new Error(body?.message || `Request failed (${res.status})`);
  }

  return text ? JSON.parse(text) : {};
}

function extractArrayFromResponse(data, keys = []) {
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
    if (Array.isArray(data?.data?.[k])) return data.data[k];
    if (Array.isArray(data?.result?.[k])) return data.result[k];
  }
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

function pickId(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v?._id || v?.id || "";
}
function pickName(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v?.name || "";
}
function meansSame(a, b) {
  return normalizeSpaces(a).toLowerCase() === normalizeSpaces(b).toLowerCase();
}

/** Voucher helpers */
function normalizeVoucherCode(v) {
  return normalizeSpaces(v).toUpperCase();
}

function parseVoucherTextToList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(normalizeVoucherCode).filter(Boolean);
  return String(raw)
    .split(",")
    .map((x) => normalizeVoucherCode(x))
    .filter(Boolean);
}

function uniqueList(arr) {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const k = normalizeVoucherCode(x);
    if (!k) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/** -------------------------------------------
 * Component
 * ------------------------------------------ */
export default function UserForm({ user, onClose, onSaved }) {
  // ✅ Distributor list (School model)
  const [distributors, setDistributors] = useState([]); // [{_id, name}]
  const [distributorsLoading, setDistributorsLoading] = useState(false);

  const token = useMemo(() => getStore().getItem("idToken"), []);
  const isEdit = Boolean(user && (user.id || user._id));

  // ✅ init distributor from user (edit mode)
  const initialDistributorId = useMemo(() => {
    const fromUser =
      user?.schoolId ||
      user?.school_id ||
      user?.school ||
      user?._school ||
      user?.distributorId ||
      user?.distributor_id ||
      user?.distributor ||
      null;

    return pickId(fromUser);
  }, [user]);

  const initialDistributorName = useMemo(() => {
    const fromUser =
      user?.schoolId ||
      user?.school ||
      user?._school ||
      user?.distributor ||
      null;

    return normalizeSpaces(
      user?.schoolName ||
        user?.school_name ||
        user?.distributorName ||
        user?.distributor_name ||
        pickName(fromUser) ||
        ""
    );
  }, [user]);

  // -------------------------
  // formData
  // -------------------------
  const [formData, setFormData] = useState(() => {
    const vouchersRaw =
      user?.vouchers ?? user?.voucherCodes ?? user?.voucherList ?? null;

    let vouchersText = "";
    if (Array.isArray(vouchersRaw)) vouchersText = vouchersRaw.join(", ");
    else if (typeof vouchersRaw === "string") vouchersText = vouchersRaw;

    return {
      name: user?.name || "",
      email: user?.email || "",
      role: user?.role || "generalUser",

      distributor_id: initialDistributorId || "",
      distributor_name: initialDistributorName || "",

      vouchers: vouchersText, // legacy field (we'll send chips)
    };
  });

  // -------------------------
  // Voucher chips state (TOP LEVEL ✅)
  // -------------------------
  const [voucherInput, setVoucherInput] = useState("");
  const [voucherChips, setVoucherChips] = useState(() => {
    const vouchersRaw =
      user?.vouchers ?? user?.voucherCodes ?? user?.voucherList ?? null;
    return uniqueList(parseVoucherTextToList(vouchersRaw));
  });

  // Sync chips when switching user (edit another row)
  useEffect(() => {
    const vouchersRaw =
      user?.vouchers ?? user?.voucherCodes ?? user?.voucherList ?? null;
    setVoucherChips(uniqueList(parseVoucherTextToList(vouchersRaw)));
    setVoucherInput("");

    // also sync distributor display
    setFormData((prev) => ({
      ...prev,
      distributor_id: initialDistributorId || "",
      distributor_name: initialDistributorName || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id]);

  const addVouchers = (raw) => {
    const list = uniqueList(parseVoucherTextToList(raw));
    if (!list.length) return;
    setVoucherChips((prev) => uniqueList([...prev, ...list]));
    setVoucherInput("");
  };

  const removeVoucher = (code) => {
    const target = normalizeVoucherCode(code);
    setVoucherChips((prev) =>
      prev.filter((x) => normalizeVoucherCode(x) !== target)
    );
  };

  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  // -------------------------
  // Distributor dropdown (create mode only)
  // -------------------------
  const [distOpen, setDistOpen] = useState(false);
  const distWrapRef = useRef(null);

  const [distQuery, setDistQuery] = useState("");
  const [distHighlighted, setDistHighlighted] = useState(0);

  const filteredDistributors = useMemo(() => {
    const q = normalizeSpaces(distQuery).toLowerCase();
    if (!q) return distributors || [];
    return (distributors || []).filter((d) =>
      String(d.name || "").toLowerCase().includes(q)
    );
  }, [distributors, distQuery]);

  const hasTyped = normalizeSpaces(distQuery).length > 0;

  const top3Distributors = useMemo(() => {
    if (!hasTyped) return [];
    return filteredDistributors.slice(0, 3);
  }, [filteredDistributors, hasTyped]);

  const exactMatch = useMemo(() => {
    const q = normalizeSpaces(distQuery).toLowerCase();
    if (!q) return null;
    return (distributors || []).find(
      (d) => normalizeSpaces(d.name).toLowerCase() === q
    );
  }, [distributors, distQuery]);

  const canCreateNewDistributor = useMemo(() => {
    const q = normalizeSpaces(distQuery);
    if (!q) return false;
    return !exactMatch;
  }, [distQuery, exactMatch]);

  const selectDistributor = (d) => {
    setFormData((prev) => ({
      ...prev,
      distributor_id: String(d._id),
      distributor_name: d.name || "",
    }));
    setDistQuery("");
    setDistOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!distOpen) return;
      const el = distWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setDistOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [distOpen]);

  // -------------------------
  // Load distributors
  // -------------------------
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) return;

      try {
        setDistributorsLoading(true);
        const data = await fetchJson(
          `${BASE_URL}/api/admin/schools?limit=500`,
          token
        );

        const rows = extractArrayFromResponse(data, ["schools"])
          .map((s) => ({
            _id: s?._id || s?.id || "",
            name: s?.name || "",
          }))
          .filter((s) => s._id && s.name);

        if (!cancelled) setDistributors(rows);
      } catch (e) {
        console.error("[UserForm] load distributors failed:", e);
        if (!cancelled) setDistributors([]);
      } finally {
        if (!cancelled) setDistributorsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ✅ If distributor_id exists but name missing, fill after list loads
  useEffect(() => {
    if (!formData.distributor_id) return;

    const found = (distributors || []).find(
      (d) => String(d._id) === String(formData.distributor_id)
    );

    if (found?.name && !meansSame(found.name, formData.distributor_name)) {
      setFormData((prev) => ({ ...prev, distributor_name: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributors, formData.distributor_id]);

  // keep distQuery aligned (create mode only)
  useEffect(() => {
    if (isEdit) return;
    if (!distOpen && formData.distributor_name && !distQuery) {
      setDistQuery(formData.distributor_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.distributor_name, isEdit]);

  const debugPingAdminUsers = async (tokenValue) => {
    try {
      const pingUrl = `${BASE_URL}/api/admin/users/debug-ping`;
      const pingRes = await fetch(pingUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${tokenValue}` },
      });
      await pingRes.text();
    } catch (err) {
      console.error("[UserForm][DebugPing] error:", err);
    }
  };

  const parseErrorMessage = (resStatus, rawText) => {
    let errBody = {};
    try {
      errBody = rawText ? JSON.parse(rawText) : {};
    } catch {
      errBody = {};
    }

    const fallback =
      resStatus === 400
        ? "Validasi gagal saat menyimpan pengguna."
        : `Gagal menyimpan pengguna. (status ${resStatus})`;

    return errBody?.message || fallback;
  };

  const handleRoleChange = (value) => {
    if (value === "admin" || value === "financeAdmin") {
      // clear chips too
      setVoucherChips([]);
      setVoucherInput("");
    }

    setFormData((prev) => {
      if (value === "admin" || value === "financeAdmin") {
        return {
          ...prev,
          role: value,
          distributor_id: "",
          distributor_name: "",
          vouchers: "",
        };
      }
      return { ...prev, role: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailError("");

    const toastId = toast.loading("Menyimpan pengguna...");

    try {
      const store = getStore();
      const tokenValue = store.getItem("idToken");

      if (!tokenValue) {
        toast.update(toastId, {
          render: "Sesi login kamu sudah habis. Silakan login ulang.",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      await debugPingAdminUsers(tokenValue);

      const isAdmin = formData.role === "admin";
      const isFinance = formData.role === "financeAdmin";
      const needsDistributor = !(isAdmin || isFinance);

      const distributorId = String(formData.distributor_id || "").trim();
      const typedName = normalizeSpaces(
        distQuery || formData.distributor_name || ""
      );

      if (needsDistributor && !isEdit && !distributorId && !typedName) {
        toast.update(toastId, {
          render: "Distributor wajib dipilih / diketik.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      const distributorNameFromPick =
        (distributors || []).find((d) => String(d._id) === distributorId)
          ?.name || "";

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,

        // ✅ LOCK distributor on edit: always use original
        schoolId: !needsDistributor
          ? null
          : isEdit
            ? initialDistributorId || null
            : distributorId || null,

        // ✅ Only allow creating/typing distributor name in CREATE mode
        schoolName: !needsDistributor
          ? null
          : isEdit
            ? null
            : distributorId
              ? distributorNameFromPick || null
              : typedName || null,

        // ✅ send from chips
        vouchers: needsDistributor ? voucherChips.join(",") : "",
      };

      const userId = user?.id || user?._id;

      const url = isEdit
        ? `${BASE_URL}/api/admin/users/${userId}`
        : `${BASE_URL}/api/admin/users`;

      const method = isEdit ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenValue}`,
        },
        body: JSON.stringify(payload),
      });

      const rawText = await res.text();

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          toast.update(toastId, {
            render:
              "Akses ditolak / sesi kamu habis. Silakan login ulang lalu coba lagi.",
            type: "error",
            isLoading: false,
            autoClose: 3500,
          });
          setLoading(false);
          return;
        }

        if (res.status === 409) {
          let errBody = {};
          try {
            errBody = rawText ? JSON.parse(rawText) : {};
          } catch {
            errBody = {};
          }

          const msg =
            errBody?.message ||
            "Email ini sudah terdaftar sebagai pengguna lain.";

          setEmailError(msg);

          toast.update(toastId, {
            render: msg,
            type: "error",
            isLoading: false,
            autoClose: 3500,
          });

          setLoading(false);
          return;
        }

        const msg = parseErrorMessage(res.status, rawText);
        toast.update(toastId, {
          render: msg,
          type: "error",
          isLoading: false,
          autoClose: 3500,
        });
        setLoading(false);
        return;
      }

      const data = rawText ? JSON.parse(rawText) : {};
      const savedUser = data.user || data;

      onSaved?.(savedUser);

      toast.update(toastId, {
        render: isEdit
          ? "Pengguna berhasil diperbarui."
          : "Pengguna berhasil dibuat.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });

      onClose?.();
      setLoading(false);
    } catch (err) {
      console.error("[UserForm] submit error:", err);
      toast.update(toastId, {
        render: "Terjadi kesalahan saat menyimpan pengguna.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
      setLoading(false);
    }
  };

  const isAdmin = formData.role === "admin";
  const isFinance = formData.role === "financeAdmin";
  const hideDistributorVoucher = isAdmin || isFinance;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-900">
            {user ? "Edit Pengguna" : "Tambah Pengguna"}
          </h3>
          <button
            onClick={onClose}
            type="button"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-88px)]"
        >
          {/* NAMA */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nama Lengkap <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nama lengkap"
            />
          </div>

          {/* EMAIL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                emailError ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="email@example.com"
            />
            {emailError && (
              <p className="mt-1 text-xs text-red-500">{emailError}</p>
            )}
          </div>

          {/* ROLE */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="generalUser">Pengguna Umum</option>
              <option value="student">Siswa</option>
              <option value="teacher">Guru</option>
              <option value="principal">Kepala Sekolah</option>
              <option value="admin">Admin</option>
              <option value="financeAdmin">Finance</option>
            </select>
          </div>

          {/* Distributor */}
          {!hideDistributorVoucher && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distributor <span className="text-red-500">*</span>
              </label>

              {isEdit ? (
                <input
                  value={formData.distributor_name || "-"}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700"
                />
              ) : (
                <div ref={distWrapRef} className="relative">
                  <input
                    value={distQuery}
                    onFocus={() => setDistHighlighted(0)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDistQuery(v);
                      setDistOpen(Boolean(normalizeSpaces(v)));
                      setDistHighlighted(0);

                      setFormData((prev) => ({
                        ...prev,
                        distributor_id: "",
                        distributor_name: v,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setDistOpen(false);
                        return;
                      }
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setDistHighlighted((x) =>
                          Math.min(x + 1, Math.max(top3Distributors.length - 1, 0))
                        );
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setDistHighlighted((x) => Math.max(x - 1, 0));
                      }
                      if (e.key === "Enter") {
                        e.preventDefault();

                        if (exactMatch) {
                          selectDistributor(exactMatch);
                          return;
                        }
                        const pick = top3Distributors[distHighlighted];
                        if (pick) {
                          selectDistributor(pick);
                          return;
                        }
                        setDistOpen(false);
                      }
                    }}
                    placeholder="Ketik nama distributor..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />

                  {hasTyped &&
                    distOpen &&
                    !distributorsLoading &&
                    top3Distributors.length > 0 && (
                      <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                        <div className="max-h-56 overflow-y-auto">
                          {top3Distributors.map((d, idx) => (
                            <button
                              key={d._id}
                              type="button"
                              onClick={() => selectDistributor(d)}
                              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                idx === distHighlighted ? "bg-gray-50" : ""
                              } ${
                                String(d._id) ===
                                String(formData.distributor_id || "")
                                  ? "bg-blue-50 text-blue-700"
                                  : "text-gray-800 hover:bg-gray-50"
                              }`}
                            >
                              {d.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  {canCreateNewDistributor && normalizeSpaces(distQuery) && (
                    <p className="mt-1 text-xs text-gray-500">
                      Akan dibuat baru saat disimpan:{" "}
                      <span className="font-medium text-gray-700">
                        "{normalizeSpaces(distQuery)}"
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* VOUCHERS (chips add/remove) */}
          {!hideDistributorVoucher && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher
              </label>

              {/* Chips */}
              <div className="flex flex-wrap gap-2 mb-2">
                {voucherChips.length === 0 && (
                  <span className="text-xs text-gray-400">
                    Belum ada voucher.
                  </span>
                )}

                {voucherChips.map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm border border-gray-200"
                  >
                    {v}
                    <button
                      type="button"
                      onClick={() => removeVoucher(v)}
                      className="text-gray-500 hover:text-gray-800"
                      aria-label={`hapus voucher ${v}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {/* Add input */}
              <div className="flex gap-2">
                <input
                  value={voucherInput}
                  onChange={(e) => setVoucherInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addVouchers(voucherInput);
                    }
                    if (e.key === ",") {
                      e.preventDefault();
                      addVouchers(voucherInput);
                    }
                  }}
                  placeholder="Ketik voucher lalu Enter (contoh: VCHR001)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <button
                  type="button"
                  onClick={() => addVouchers(voucherInput)}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors"
                >
                  Tambah
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-1">
                Voucher hanya bisa <b>ditambah</b> atau <b>dihapus</b>. Pisahkan
                dengan koma kalau mau paste banyak.
              </p>
            </div>
          )}

          {/* ACTIONS */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Menyimpan..." : user ? "Perbarui" : "Tambah"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
